var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, makeObservable, observable } from 'mobx';
import { URLData } from '@telia-common-components/shared';
import API from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
import { TELIA_TV_NETFLIX_PREMIUM } from '../util/TvServicesUtil';
class TvPackageStore {
    constructor(tier, app, lang) {
        this.tvPackages = null;
        this.status = FetchStatus.UNINITIALIZED;
        this.isModalOpen = false;
        this.selectedPackage = '';
        this.existingPackage = '';
        this.customerPrices = [];
        this.viewRequirements = [];
        this.excludedApps = [TELIA_TV_NETFLIX_PREMIUM];
        this.fetchTvPackages = async (basketId, productId, productCategoryCode) => {
            try {
                this.status = FetchStatus.FETCHING;
                if (!productId) {
                    await this.fetchNewPackages(basketId, productCategoryCode);
                }
                else {
                    await this.fetchExistingPackages(basketId, productId);
                }
                this.status = FetchStatus.FETCHED;
            }
            catch (e) {
                this.status = FetchStatus.ERROR;
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.fetchNewPackages = async (basketId, productCategoryCode) => {
            const [packages, prices] = await Promise.all([
                this.api.get('/tv-comparison/packages/simple', {
                    params: { productCategoryCode, basketId },
                }),
                !!basketId
                    ? this.api.get('/tv-comparison/packages/prices', {
                        params: { basketId },
                    })
                    : Promise.resolve({ data: null }),
            ]);
            this.existingPackage = '';
            this.tvPackages = packages.data;
            if (prices === null || prices === void 0 ? void 0 : prices.data) {
                this.tvPackages.packagePrices = prices.data;
            }
        };
        this.fetchExistingPackages = async (basketId, productId) => {
            var _a, _b, _c;
            const existing = await this.api.get(`/tv-comparison/packages/simple/product/${productId}`, {
                params: { basketId },
            });
            let substitutions;
            if ((_a = existing.data) === null || _a === void 0 ? void 0 : _a.offeringId) {
                substitutions = await this.api.get('/tv-comparison/packages/simple/substitution', {
                    params: { offeringId: existing.data.offeringId },
                });
            }
            this.tvPackages = (substitutions === null || substitutions === void 0 ? void 0 : substitutions.data) || existing.data;
            this.existingPackage = (_c = (_b = existing.data) === null || _b === void 0 ? void 0 : _b.packagePrices[0]) === null || _c === void 0 ? void 0 : _c.name;
        };
        this.fetchPackagePrices = async (basketId) => {
            if (!basketId) {
                this.customerPrices = [];
                return;
            }
            try {
                const prices = await this.api.get('/tv-comparison/packages/prices', {
                    params: { basketId },
                    withCredentials: true,
                });
                this.customerPrices = prices.data;
            }
            catch (e) {
                if (this.app === 'selfservice') {
                    this.status = FetchStatus.ERROR;
                }
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.fetchTvPackageViewRequirements = async (lang) => {
            try {
                const result = await this.api.get(`/tv-comparison/packages/view-requirements/${lang}`);
                this.viewRequirements = result.data;
            }
            catch (e) {
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.generateCustomerBasketId = async (customerId) => {
            if (!customerId) {
                return undefined;
            }
            try {
                const result = await this.api.get('/tv-comparison/basket-id', {
                    withCredentials: true,
                });
                return result.data;
            }
            catch (e) {
                ErrorUtil.pushErrorToTeliaRumAndLog(e);
            }
        };
        this.generateBasketIdAndFetchPackagePrices = async (shouldGenerate, customerId) => {
            let generatedBasketId;
            if (shouldGenerate) {
                generatedBasketId = await this.generateCustomerBasketId(customerId);
            }
            this.fetchPackagePrices(generatedBasketId);
        };
        this.getIncludedOfferings = (items, productOfferingCode) => {
            return items
                .filter((item) => item.includedInPackage[productOfferingCode] && !this.excludedApps.includes(item.productOfferingCode))
                .map((item) => item.productOfferingCode);
        };
        this.getTvPackageInclusions = (productOfferingCode) => {
            return {
                additionalServices: this.getIncludedOfferings(this.tvPackages.additionalServices, productOfferingCode),
                tvApps: this.getIncludedOfferings(this.tvPackages.apps, productOfferingCode),
                themePackages: this.getIncludedOfferings(this.tvPackages.themePackages, productOfferingCode),
            };
        };
        makeObservable(this);
        this.app = app;
        this.setUrlData(app, tier, lang);
        this.api = API(this.urlData.commonAPIBaseURL, { withCredentials: true });
    }
    setUrlData(app, tier, lang) {
        this.urlData = new URLData({ app: app, tier: tier, lang: lang });
        this.setSelfServiceURL(this.urlData.selfServiceURL);
    }
    setSelfServiceURL(url) {
        this.selfServiceUrl = url;
    }
    get tvPackageCards() {
        var _a;
        return (_a = this.tvPackages) === null || _a === void 0 ? void 0 : _a.packageNames.map((pn) => {
            var _a, _b;
            const packagePrice = this.tvPackages.packagePrices.find((pp) => pp.name === pn);
            return {
                id: packagePrice.name,
                title: pn,
                prices: packagePrice,
                channels: (_b = (_a = this.tvPackages.packageSizes) === null || _a === void 0 ? void 0 : _a.find((ps) => ps.name === pn)) === null || _b === void 0 ? void 0 : _b.size,
                viewers: this.tvPackages.viewerSlots[pn].slots,
                categoriesList: this.tvPackages.categoriesList[pn],
                additionalServices: this.tvPackages.additionalServices
                    .filter((as) => as.includedInPackage[pn])
                    .map((as) => {
                    return {
                        id: as.productOfferingCode,
                        title: as.productOfferingCode,
                    };
                }),
                tvApps: this.tvPackages.apps
                    .filter((app) => app.includedInPackage[pn])
                    .map((app) => {
                    return {
                        id: app.productOfferingCode,
                        title: app.productOfferingCode,
                    };
                }),
                themePackages: this.tvPackages.themePackages
                    .filter((tp) => tp.includedInPackage[pn])
                    .map((tp) => {
                    return {
                        id: tp.productOfferingCode,
                        title: tp.productOfferingCode,
                    };
                }),
            };
        });
    }
}
__decorate([
    observable
], TvPackageStore.prototype, "tvPackages", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "status", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "isModalOpen", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "selectedPackage", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "urlData", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "selfServiceUrl", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "existingPackage", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "customerPrices", void 0);
__decorate([
    observable
], TvPackageStore.prototype, "viewRequirements", void 0);
__decorate([
    action
], TvPackageStore.prototype, "fetchTvPackages", void 0);
__decorate([
    action
], TvPackageStore.prototype, "fetchNewPackages", void 0);
__decorate([
    action
], TvPackageStore.prototype, "fetchExistingPackages", void 0);
__decorate([
    action
], TvPackageStore.prototype, "fetchPackagePrices", void 0);
__decorate([
    action
], TvPackageStore.prototype, "fetchTvPackageViewRequirements", void 0);
__decorate([
    action
], TvPackageStore.prototype, "setUrlData", null);
__decorate([
    action
], TvPackageStore.prototype, "setSelfServiceURL", null);
__decorate([
    computed
], TvPackageStore.prototype, "tvPackageCards", null);
export default TvPackageStore;
